import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Upload, Image, Palette } from "lucide-react";
import GeneralSettingsSkeleton from "./skeletons/GeneralSettingsSkeleton";
import { useGeneralSettingsData } from "@/lib/settings/generalSettingsData";

export function GeneralSettingsTab() {
  const {
    settings,
    setSettings,
    handleColorChange,
    isGeneralLoading,
    saveGeneralSettings,
    handleLogoUpload,
    logoError,
    isDarkMode,
  } = useGeneralSettingsData();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      organizationName: "",
      organizationAddress: "",
      phoneNumber: "",
      email: "",
    },
  });

  useEffect(() => {
    if (settings) {
      reset({
        organizationName: settings.organizationName || "",
        organizationAddress: settings.organizationAddress || "",
        phoneNumber: settings.phoneNumber || "",
        email: settings.email || "",
      });
    }
  }, [settings, reset]);

  const onSubmit = (data: any) => {
    setSettings((prev: any) => ({ ...prev, ...data }));
    saveGeneralSettings({ ...settings, ...data });
  };

  if (isGeneralLoading) return <GeneralSettingsSkeleton />;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
      <div>
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          General Settings
        </h2>

        <div className="space-y-4">
          {/* Organization Info */}
          <h3
            className={`font-medium transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Organization Information
          </h3>
          {/* Organization Name */}
          <div className="space-y-2">
            <Label
              htmlFor="org-name"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Organization Name
            </Label>
            <Input
              id="org-name"
              {...register("organizationName", {
                required: "Organization name is required",
                minLength: { value: 2, message: "Too short" },
              })}
            />
            {errors.organizationName && (
              <p className="text-sm text-red-500">
                {errors.organizationName.message}
              </p>
            )}
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label
              htmlFor="address"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Organization Address
            </Label>
            <Textarea
              id="address"
              rows={3}
              placeholder="Invatech Health 442-450 Stapleton Rd..."
              {...register("organizationAddress", {
                required: "Address is required",
              })}
            />
            {errors.organizationAddress && (
              <p className="text-sm text-red-500">
                {errors.organizationAddress.message}
              </p>
            )}
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="phone"
                className={isDarkMode ? "text-gray-200" : "text-gray-900"}
              >
                Phone Number
              </Label>
              <Input
                id="phone"
                placeholder="+44 (0) 777 649 55 86"
                {...register("phoneNumber", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^[0-9+\-()\s]*$/,
                    message: "Invalid phone format",
                  },
                })}
              />
              {errors.phoneNumber && (
                <p className="text-sm text-red-500">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                placeholder="contact@titanverse.health"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Invalid email format",
                  },
                })}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>
          </div>

          {/* Branding Section */}
          <div
            className={`space-y-4 pt-4 border-t transition-colors duration-300 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <h3
              className={`font-medium flex items-center gap-2 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              <Palette className="w-4 h-4" />
              Company Branding
            </h3>

            {/* Logo Upload */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="company-logo"
                  className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                >
                  Company Logo
                </Label>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Upload your company logo (recommended size: 200x80px)
                </p>

                <div className="flex items-center gap-4">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-24 h-10 border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden transition-colors duration-300 ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-800"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {settings.organizationLogo ? (
                        <img
                          src={settings.organizationLogo}
                          alt="Logo preview"
                          className="object-contain w-full h-full"
                        />
                      ) : (
                        <Image
                          className={`w-5 h-5 ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                      )}
                    </div>
                    <p
                      className={`text-xs mt-1 text-center ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Preview
                    </p>
                  </div>

                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() =>
                        document.getElementById("logo-upload")?.click()
                      }
                      className={`w-full justify-center ${
                        isDarkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : ""
                      }`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {settings.organizationLogo
                        ? "Change Logo"
                        : "Upload Logo"}
                    </Button>

                    {logoError && (
                      <p className="mt-2 text-sm text-red-600">
                        {logoError}
                      </p>
                    )}

                    {settings.organizationLogo && (
                      <Button
                        variant="ghost"
                        type="button"
                        size="sm"
                        onClick={() =>
                          setSettings((prev) => ({
                            ...prev,
                            organizationLogo: "",
                          }))
                        }
                        className="w-full mt-2 text-red-600 hover:text-red-700"
                      >
                        Remove Logo
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Colors */}
              <div className="grid grid-cols-2 gap-4">
                {/* Primary Color */}
                <div className="space-y-2">
                  <Label
                    htmlFor="primary-color"
                    className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                  >
                    Primary Color
                  </Label>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Main brand color for buttons and accents
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      id="primary-color"
                      value={settings.primaryColor}
                      onChange={(e) =>
                        handleColorChange("primaryColor")({
                          hex: e.target.value,
                        })
                      }
                      className={`w-12 h-10 rounded border cursor-pointer ${
                        isDarkMode ? "border-gray-600" : "border-gray-300"
                      }`}
                    />
                    <Input
                      value={settings.primaryColor}
                      onChange={(e) =>
                        handleColorChange("primaryColor")({
                          hex: e.target.value,
                        })
                      }
                      placeholder="#8b5cf6"
                      className={`flex-1 font-mono text-sm ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-600 text-white"
                          : "bg-white"
                      }`}
                    />
                  </div>
                </div>

                {/* Secondary Color */}
                <div className="space-y-2">
                  <Label
                    htmlFor="secondary-color"
                    className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                  >
                    Secondary Color
                  </Label>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Supporting color for backgrounds and borders
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      id="secondary-color"
                      value={settings.secondaryColor}
                      onChange={(e) =>
                        handleColorChange("secondaryColor")({
                          hex: e.target.value,
                        })
                      }
                      className={`w-12 h-10 rounded border cursor-pointer ${
                        isDarkMode ? "border-gray-600" : "border-gray-300"
                      }`}
                    />
                    <Input
                      value={settings.secondaryColor}
                      onChange={(e) =>
                        handleColorChange("secondaryColor")({
                          hex: e.target.value,
                        })
                      }
                      placeholder="#64748b"
                      className={`flex-1 font-mono text-sm ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-600 text-white"
                          : "bg-white"
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Color Preview */}
              <div className="space-y-2">
                <Label
                  className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                >
                  Color Preview
                </Label>
                <div
                  className={`p-4 border rounded-lg ${
                    isDarkMode
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <Button
                      variant="outline"
                      type="button"
                      style={{
                        borderColor: settings.primaryColor,
                        color: settings.primaryColor,
                      }}
                      className={`${
                        isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                      }`}
                    >
                      Primary Button
                    </Button>
                    <Button
                      variant="outline"
                      type="button"
                      style={{
                        borderColor: settings.secondaryColor,
                        color: settings.secondaryColor,
                      }}
                      className={`${
                        isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                      }`}
                    >
                      Secondary Button
                    </Button>
                    <div
                      className="w-8 h-8 border-2 rounded-full"
                      style={{
                        backgroundColor: settings.primaryColor,
                        borderColor: settings.secondaryColor,
                      }}
                    ></div>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: settings.primaryColor }}
                      ></div>
                      <span
                        className="text-sm font-medium"
                        style={{ color: settings.primaryColor }}
                      >
                        Primary Text
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: settings.secondaryColor }}
                      ></div>
                      <span
                        className="text-sm"
                        style={{ color: settings.secondaryColor }}
                      >
                        Secondary Text
                      </span>
                    </div>
                  </div>
                  <p
                    className={`text-xs mt-3 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Preview how your brand colors will look in the interface
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-gray-200">
        <Button
          type="submit"
          className={`bg-slate-900 hover:bg-slate-800 ${
            isGeneralLoading ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <Save className="w-4 h-4 mr-2" />
          {isGeneralLoading ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </form>
  );
}
